import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { API_URL, getAuthHeader, formatDateTime } from '@/lib/utils';
import { 
  Users, Briefcase, GraduationCap, Package, 
  MessageSquare, FileText, TrendingUp, ArrowRight,
  Phone, Mail
} from 'lucide-react';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API_URL}/dashboard/stats`, {
          headers: getAuthHeader()
        });
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  const statCards = [
    { title: 'Total Leads', value: stats?.total_leads || 0, icon: Users, color: 'bg-blue-500', link: '/admin/leads' },
    { title: 'New Leads', value: stats?.new_leads || 0, icon: TrendingUp, color: 'bg-green-500', link: '/admin/leads' },
    { title: 'Services', value: stats?.total_services || 0, icon: Briefcase, color: 'bg-purple-500', link: '/admin/services' },
    { title: 'Courses', value: stats?.total_courses || 0, icon: GraduationCap, color: 'bg-orange-500', link: '/admin/courses' },
    { title: 'Products', value: stats?.total_products || 0, icon: Package, color: 'bg-indigo-500', link: '/admin/products' },
    { title: 'Blog Posts', value: stats?.total_blogs || 0, icon: FileText, color: 'bg-pink-500', link: '/admin/blogs' },
  ];

  return (
    <div data-testid="admin-dashboard">
      <div className="mb-8">
        <h1 className="font-manrope text-2xl md:text-3xl font-bold text-[#1a2a5e]">Dashboard</h1>
        <p className="text-gray-500 mt-1">Welcome back! Here's an overview of your website.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {statCards.map((stat, index) => (
          <Link key={index} to={stat.link}>
            <Card className="hover:shadow-lg transition-shadow cursor-pointer">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 ${stat.color} rounded-lg flex items-center justify-center`}>
                    <stat.icon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1a2a5e]">{stat.value}</p>
                    <p className="text-xs text-gray-500">{stat.title}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Recent Leads */}
      <Card className="mb-8">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold text-[#1a2a5e]">Recent Leads</CardTitle>
          <Button asChild variant="outline" size="sm">
            <Link to="/admin/leads">
              View All <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </CardHeader>
        <CardContent>
          {stats?.recent_leads && stats.recent_leads.length > 0 ? (
            <div className="space-y-4">
              {stats.recent_leads.map((lead) => (
                <div key={lead.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-[#1a2a5e] rounded-full flex items-center justify-center text-white font-bold">
                      {lead.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium text-[#1a2a5e]">{lead.name}</p>
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {lead.email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {lead.phone}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant={lead.status === 'new' ? 'default' : 'secondary'}>
                      {lead.status}
                    </Badge>
                    <p className="text-xs text-gray-400 mt-1">{formatDateTime(lead.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No leads yet</p>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#1a2a5e]">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button asChild variant="outline" className="h-20 flex-col gap-2">
              <Link to="/admin/services">
                <Briefcase className="h-6 w-6 text-[#1a2a5e]" />
                <span>Add Service</span>
              </Link>
            </Button>
            <Button asChild variant="outline" className="h-20 flex-col gap-2">
              <Link to="/admin/courses">
                <GraduationCap className="h-6 w-6 text-[#1a2a5e]" />
                <span>Add Course</span>
              </Link>
            </Button>
            <Button asChild variant="outline" className="h-20 flex-col gap-2">
              <Link to="/admin/blogs">
                <FileText className="h-6 w-6 text-[#1a2a5e]" />
                <span>Write Blog</span>
              </Link>
            </Button>
            <Button asChild variant="outline" className="h-20 flex-col gap-2">
              <Link to="/admin/testimonials">
                <MessageSquare className="h-6 w-6 text-[#1a2a5e]" />
                <span>Add Testimonial</span>
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
